import type { StripeError } from "@stripe/stripe-js";

import { paywallCheckoutAttemptsApi } from "@/react/api/paywallCheckoutAttemptsApi";
import { notifyBugsnag } from "@/react/helpers/bugsnagHelpers";

import { CheckoutError } from "../../CheckoutError";
import { constructReturnUrl, constructReturnUrlPath } from "../../helpers";
import type { useGetStripeElements } from "../../useStripeElements";
import { buildConfirmParams, createSetupIntent } from "../helpers/stripe";
import type { MemberAddressState, OnStageChange } from "../types";
import { stages } from "../types";
import { cancelDeferredAttempt } from "./cancelDeferredAttempt";
import type { useConfirmPayment } from "./useConfirmPayment";
import type { Attempt } from "./useCreateAttempt";

// Params shared by both submit paths — computed once in the hook and injected to avoid duplication.
export interface SharedSubmitParams {
  billingInfoAttributes: Record<string, unknown>;
  billingMemberAddress: MemberAddressState;
  createAttempt: (data: Record<string, unknown>) => Promise<Attempt>;
  data: Record<string, unknown>;
  isExpressMode: boolean;
  onStageChange: OnStageChange;
  paymentMethodType: string;
  replaceUrl: (url: string) => void;
  sessionId?: number;
}

export interface SetupIntentSubmitParams extends SharedSubmitParams {
  checkoutIntentToken?: string;
  communityId: number;
  getStripeElements: ReturnType<typeof useGetStripeElements>;
  paywallPriceId: number;
}

export interface PaymentIntentSubmitParams extends SharedSubmitParams {
  confirmPayment: ReturnType<typeof useConfirmPayment>;
}

interface HandleSetupIntentErrorParams {
  attempt: Attempt;
  error: StripeError;
  navigateToVerification: () => void;
}

const handleSetupIntentError = async ({
  attempt,
  error,
  navigateToVerification,
}: HandleSetupIntentErrorParams): Promise<void> => {
  // setup_intent_unexpected_state means the SI is already confirmed — this happens in
  // production when a webhook processes the SI before the browser confirms it (race
  // condition), and in VCR tests where the cassette replays a stale confirmed SI.
  // In both cases the SI is succeeded; navigate to verification so the server can
  // resolve it via ResolveSetupIntentService.
  if (error.code === "setup_intent_unexpected_state") {
    navigateToVerification();
    return;
  }

  if (attempt.isSetupDeferred) {
    const cancellation = await cancelDeferredAttempt(attempt.id, error);
    if (cancellation === "navigate" || cancellation === "reconcile") {
      navigateToVerification();
      return;
    }
  } else {
    // The attempt was created above but confirmSetup failed. Cancel best-effort so the
    // session step is reset and the member can retry. Swallow cancel errors to avoid
    // masking the original Stripe error, but report cancel failures to Bugsnag so stuck
    // attempts don't go unnoticed.
    try {
      await paywallCheckoutAttemptsApi.cancel(attempt.id);
    } catch (cancelError) {
      notifyBugsnag(
        cancelError instanceof Error
          ? cancelError
          : new Error(
              "submitSetupIntent: attempt cancel failed after confirmSetup error",
            ),
      );
    }
  }

  throw CheckoutError.fromStripeError(
    "useSubmitAttempt.submitAttempt.confirmSetup",
    error,
  );
};

export class PaymentIntentSubmitError extends Error {
  readonly attempt: Attempt;
  readonly returnUrl: string;
  readonly navigateToVerification: () => void;
  readonly checkoutError: CheckoutError;

  constructor(
    checkoutError: CheckoutError,
    attempt: Attempt,
    returnUrl: string,
    navigateToVerification: () => void,
  ) {
    super(checkoutError.message, { cause: checkoutError });
    this.name = "PaymentIntentSubmitError";
    this.checkoutError = checkoutError;
    this.attempt = attempt;
    this.returnUrl = returnUrl;
    this.navigateToVerification = navigateToVerification;
  }
}

export async function submitSetupIntent({
  billingInfoAttributes,
  billingMemberAddress,
  checkoutIntentToken,
  communityId,
  createAttempt,
  data,
  getStripeElements,
  onStageChange,
  paymentMethodType,
  paywallPriceId,
  replaceUrl,
  sessionId,
}: SetupIntentSubmitParams): Promise<void> {
  onStageChange({ stage: stages.settingUpPaymentMethod });
  const { setupIntentId, setupIntentClientSecret } = await createSetupIntent(
    communityId,
    paywallPriceId,
    checkoutIntentToken,
  );

  onStageChange({ stage: stages.creatingAttempt });
  const attempt = await createAttempt({
    ...data,
    community_member_billing_info_attributes: billingInfoAttributes,
    setup_intent_id: setupIntentId,
    // Clear any stale payment_method_processor_id from form data (e.g. a previously saved PM).
    // For the setup path the PM is resolved server-side on redirect return via
    // ResolveSetupIntentService — passing a stale saved PM here would corrupt the intent.
    payment_method_processor_id: undefined,
  });

  const returnUrlParams = {
    invoiceId: attempt.invoiceId,
    paymentIntentId: attempt.paymentIntentId,
    paymentMethodType,
    sessionId,
    setupIntentId:
      attempt.isSetupDeferred && !attempt.invoiceId && !attempt.paymentIntentId
        ? setupIntentId
        : undefined,
  };
  const returnUrl = constructReturnUrl(returnUrlParams);
  const returnUrlPath = constructReturnUrlPath(returnUrlParams);
  const navigateToVerification = () => {
    replaceUrl(returnUrlPath);
  };

  onStageChange({ stage: stages.confirmingAttempt });

  const { stripe, stripeElements } = getStripeElements(
    "useSubmitAttempt.submitAttempt.confirmSetup",
  );
  const confirmSetupResult = await stripe.confirmSetup({
    elements: stripeElements,
    clientSecret: setupIntentClientSecret,
    confirmParams: buildConfirmParams(billingMemberAddress, returnUrl),
    redirect: "if_required",
  });
  if (confirmSetupResult.error) {
    await handleSetupIntentError({
      attempt,
      error: confirmSetupResult.error,
      navigateToVerification,
    });
    return;
  }

  navigateToVerification();
}

// Handles the payment-intent path: card, SEPA, and other non-setup PMs.
// Self-contained: wraps any confirmPayment failure in PaymentIntentSubmitError so
// the orchestrating hook's catch block can access the partial PI state for recovery
// without relying on mutable closures.
export async function submitPaymentIntent({
  billingInfoAttributes,
  billingMemberAddress,
  confirmPayment,
  createAttempt,
  data,
  isExpressMode,
  onStageChange,
  paymentMethodType,
  replaceUrl,
  sessionId,
}: PaymentIntentSubmitParams): Promise<void> {
  onStageChange({ stage: stages.creatingAttempt });
  const attempt = await createAttempt({
    ...data,
    // Express mode (upsells): charge the member's saved/default payment method.
    // This lets the backend infer the real `payment_method_type` (e.g. sepa_debit) instead of
    // relying on the UI-driven payment method type (there is no PaymentElement in this mode).
    use_default_payment_method: isExpressMode,
    community_member_billing_info_attributes: billingInfoAttributes,
    // Clear any stale payment_method_processor_id from form data (e.g. a previously saved
    // PM). For the payment path, the PM is attached during confirmPayment via Stripe.js, not
    // during PI creation. Passing a saved PM here when the user chose a different type causes
    // Stripe to reject the PI ("incompatible with the attached PaymentMethod").
    payment_method_processor_id: undefined,
  });

  const returnUrlParams = {
    invoiceId: attempt.invoiceId,
    paymentIntentId: attempt.paymentIntentId,
    paymentMethodType,
    sessionId,
  };
  const returnUrl = constructReturnUrl({
    ...returnUrlParams,
    paymentIntentId: undefined,
  });

  const returnUrlPath = constructReturnUrlPath(returnUrlParams);
  // Navigate to verification URL - the PaywallCheckoutAttemptsStageHandler will
  // detect the URL params and trigger verification.
  const navigateToVerification = () => {
    replaceUrl(returnUrlPath);
  };

  // No payment intent = trial or $0 invoice; advance stage so canVerifyAttempt() gates pass.
  if (!attempt.paymentIntentClientSecret || !attempt.paymentIntentId) {
    onStageChange({ stage: stages.confirmingAttempt });
    navigateToVerification();
    return;
  }

  onStageChange({ stage: stages.confirmingAttempt });

  try {
    await confirmPayment({
      memberAddress: billingMemberAddress,
      paymentIntentClientSecret: attempt.paymentIntentClientSecret,
      paymentMethodType,
      returnUrl,
    });
  } catch (e: unknown) {
    const error = CheckoutError.fromUnknownError(
      "submitPaymentIntent.confirmPayment",
      e,
    );
    // Wrap with partial state so the caller's catch block can run PI recovery
    // without mutable closures.
    throw new PaymentIntentSubmitError(
      error, // checkoutError
      attempt,
      returnUrl,
      navigateToVerification,
    );
  }

  navigateToVerification();
}
